import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { SkeletonGroup } from '@shared/components/Skeleton/Skeleton';

export type WidgetTransitionProps = Readonly<{
  /**
   * When true, renders `children` with a fade-in animation.
   * When false, renders nothing (blank placeholder).
   */
  isReady: boolean;
  /**
   * Height floor held through the not-ready AND ready branches. Without it the placeholder is a
   * zero-height box, so the widget's full height lands as one layout shift when its data
   * resolves (guest welcome body measured 0.12 CLS from this alone). It must persist into the
   * ready branch because content can arrive in waves: `isReady` flips when the container query
   * resolves, but the JSON components inside load their modules in a second pass — a
   * placeholder-only floor collapses between the waves and shifts twice (measured, not
   * hypothetical). Opt-in per call site, and only for callers whose content is always at least
   * this tall — on shorter content a floor would leave permanent whitespace.
   */
  placeholderMinHeight?: BoxProps['minHeight'];
  children: React.ReactNode;
}>;

/**
 * Wraps a widget or page section so its content fades in once data resolves.
 * The placeholder is intentionally blank — we show nothing until the real
 * content is ready, then animate it in.
 *
 * See doc/improve-guest-site-perf/15-ssr-narrow-surface/PLAN.md.
 */
export const WidgetTransition: React.FC<WidgetTransitionProps> = ({ isReady, placeholderMinHeight, children }) => {
  return (
    <SkeletonGroup
      isReady={isReady}
      placeholder={null}
      width="100%"
      overrides={placeholderMinHeight ? { PlaceholderRoot: { props: { minHeight: placeholderMinHeight } }, Root: { props: { minHeight: placeholderMinHeight } } } : undefined}
    >
      {children}
    </SkeletonGroup>
  );
};

WidgetTransition.displayName = 'WidgetTransition';
